<template>
    <div>
        <ts-page-title
            :title="$t('collateralRecord.pageTitle')"
            :breadcrumb="[
                { text: $t('home'), href: '/' },
                {
                    text: $t('collateralRecord.pageTitle'),
                    active: true
                }
            ]"
        />
        <ts-panel>
            <ts-panel-wrapper
                class="tw-flex tw-items-center tw-justify-between"
            >
                <Button type="info" ghost @click="addNew">{{
                    $t('addNew')
                }}</Button>
                <div class="tw-justify-end tw-space-x-2">
                    <Input
                        search
                        v-model="search"
                        :placeholder="$t('collateralRecord.search')"
                        :style="width"
                        @on-focus="() => (width = 'width: 500px')"
                        @on-blur="() => (width = 'width: 300px')"
                    />
                </div>
            </ts-panel-wrapper>
            <Table
                :columns="columns"
                :data="resources"
                size="small"
                :loading="loading"
                max-height="700"
                stripe
            >
                <template slot-scope="{ row }" slot="loan">
                    <span v-if="row.loan_disbursement">
                        {{ row.loan_disbursement.loan_number }}
                        <br />
                        <small class="text-muted" v-if="row.loan_disbursement.customer">
                            {{ row.loan_disbursement.customer.customer_name_en }}
                        </small>
                    </span>
                </template>
                <template slot-scope="{ row }" slot="asset">
                    <span v-if="row.collateral_asset">
                        {{ row.collateral_asset.asset_name }}
                        <br />
                        <small class="text-muted">{{ row.collateral_asset.asset_code }}</small>
                    </span>
                </template>
                <template slot-scope="{ row }" slot="attached_by">
                    <span v-if="row.attached_by_user && row.attached_by_user.employee">
                        {{ row.attached_by_user.employee.employee_name_en }}
                    </span>
                </template>
                <template slot-scope="{ row }" slot="status">
                    <Tag color="success" v-if="!row.withdrawal_date">
                        {{ $t('collateralRecord.active') }}
                    </Tag>
                    <Tag color="warning" v-else>
                        {{ $t('collateralRecord.withdrawn') }}
                    </Tag>
                </template>
                <template slot-scope="{ row }" slot="action">
                    <Poptip
                        v-if="!row.withdrawal_date"
                        confirm
                        :title="$t('collateralRecord.confirmWithdraw')"
                        @on-ok="onWithdraw(row)"
                        :transfer="true"
                        :ok-text="$t('yes')"
                        :cancel-text="$t('no')"
                    >
                        <a
                            class="text-warning mr-2"
                            v-tooltip="$t('collateralRecord.withdraw')"
                            :disabled="row._withdrawing"
                        >
                            <i
                                class="fas fa-circle-notch fa-spin"
                                v-if="row._withdrawing"
                            ></i>
                            <Icon type="ios-log-out" size="20" v-else />
                        </a>
                    </Poptip>
                    <a
                        href="#"
                        class="text-primary ml-2"
                        @click.prevent="onEdit(row)"
                        v-tooltip="$t('edit')"
                        v-if="!row.withdrawal_date"
                    >
                        <Icon type="ios-create" size="20" />
                    </a>
                    <Poptip
                        confirm
                        :title="$t('areYouSure')"
                        @on-ok="onDelete(row)"
                        :transfer="true"
                        :ok-text="$t('yes')"
                        :cancel-text="$t('no')"
                    >
                        <a
                            class="ml-2 text-danger"
                            v-tooltip="$t('delete')"
                            :disabled="row._deleting"
                        >
                            <i
                                class="fas fa-circle-notch fa-spin"
                                v-if="row._deleting"
                            ></i>
                            <Icon type="ios-trash" size="20" v-else />
                        </a>
                    </Poptip>
                </template>
            </Table>
            <div class="tw-flex tw-items-center tw-justify-end tw-p-4">
                <ts-i-pagination
                    v-model="pagination"
                    @navigate="fetchData"
                ></ts-i-pagination>
            </div>
            <!-- form-action -->
            <Modal
                v-model="showForm"
                draggable
                sticky
                scrollable
                :mask="false"
                :footer-hide="true"
                :z-index="1022"
                :title="$t('collateralRecord.pageTitle')"
                width="700"
            >
                <from-action
                    ref="form_action"
                    @cancel="clearEdit"
                    @fetchData="fetchData"
                />
            </Modal>
        </ts-panel>
    </div>
</template>

<script>
import { debounce } from 'lodash'
import { mapState } from 'vuex'
import FromAction from './form'

export default {
    name: 'collateralRecordIndex',
    components: {
        FromAction
    },
    data () {
        return {
            loading: false,
            showForm: false,
            width: 'width: 300px'
        }
    },
    computed: {
        ...mapState('creditOperation/collateralRecord', [
            'resources',
            'pagination'
        ]),
        search: {
            get () {
                return this.$store.state.creditOperation.collateralRecord.search
            },
            set (newValue) {
                this.$store.commit(
                    'creditOperation/collateralRecord/SET_SEARCH',
                    newValue
                )
                this.$store.commit(
                    'creditOperation/collateralRecord/RESET_CURRENT_PAGE'
                )
            }
        },
        columns () {
            return [
                {
                    title: this.$t('collateralRecord.loan'),
                    slot: 'loan',
                    className: 'tw-whitespace-nowrap',
                    minWidth: 200
                },
                {
                    title: this.$t('collateralRecord.collateralAsset'),
                    slot: 'asset',
                    className: 'tw-whitespace-nowrap',
                    minWidth: 180
                },
                {
                    title: this.$t('collateralRecord.attachedDate'),
                    key: 'attached_date',
                    className: 'tw-whitespace-nowrap',
                    sortable: true,
                    width: 130
                },
                {
                    title: this.$t('collateralRecord.attachedBy'),
                    slot: 'attached_by',
                    className: 'tw-whitespace-nowrap',
                    width: 150
                },
                {
                    title: this.$t('collateralRecord.withdrawalDate'),
                    key: 'withdrawal_date',
                    className: 'tw-whitespace-nowrap',
                    width: 160
                },
                {
                    title: this.$t('collateralRecord.status'),
                    slot: 'status',
                    align: 'center',
                    className: 'tw-whitespace-nowrap',
                    width: 100,
                    filters: [
                        {
                            label: 'Active',
                            value: 'false'
                        },
                        {
                            label: 'Withdrawn',
                            value: 'true'
                        }
                    ],
                    filterMultiple: false,
                    filterRemote: value => {
                        this.fetchData({
                            filter: {
                                is_withdrawn: value[0]
                            }
                        })
                    }
                },
                {
                    title: this.$t('collateralRecord.notes'),
                    key: 'notes',
                    className: 'tw-whitespace-nowrap',
                    minWidth: 150,
                    ellipsis: true
                },
                {
                    title: this.$t('actions'),
                    key: 'action',
                    slot: 'action',
                    fixed: 'right',
                    align: 'center',
                    className: 'tw-space-x-2',
                    width: 130
                }
            ]
        }
    },
    methods: {
        fetchData (attributes) {
            this.loading = true
            this.$store
                .dispatch('creditOperation/collateralRecord/fetch', {
                    ...attributes
                })
                .then(() => {
                    this.loading = false
                })
                .catch(error => {
                    this.loading = false
                    this.notice({ type: 'error', text: error.message })
                })
        },
        onEdit (record) {
            this.$store.commit(
                'creditOperation/collateralRecord/SET_EDIT_DATA',
                record
            )
            this.showForm = true
        },
        clearEdit () {
            this.showForm = false
            this.$refs.form_action.clearInput()
            this.$store.commit(
                'creditOperation/collateralRecord/SET_EDIT_DATA',
                {}
            )
        },
        addNew () {
            this.$store.commit(
                'creditOperation/collateralRecord/SET_EDIT_DATA',
                {}
            )
            this.showForm = true
            this.$refs.form_action.clearInput()
        },
        async onDelete (record) {
            record._deleting = true
            this.$store
                .dispatch(
                    'creditOperation/collateralRecord/destroy',
                    record.collateral_id
                )
                .then(response => {
                    this.fetchData()
                    record._deleting = false
                    this.notice({
                        type: 'success',
                        text: response.message
                    })
                })
                .catch(error => {
                    this.notice({ type: 'error', text: error.message })
                    record._deleting = false
                })
        },
        async onWithdraw (record) {
            record._withdrawing = true
            this.$store
                .dispatch('creditOperation/collateralRecord/withdraw', {
                    id: record.collateral_id,
                    data: {}
                })
                .then(response => {
                    this.fetchData()
                    record._withdrawing = false
                    this.notice({
                        type: 'success',
                        text: response.message
                    })
                })
                .catch(error => {
                    this.notice({ type: 'error', text: error.message })
                    record._withdrawing = false
                })
        },
        notice (not) {
            this.$Notice[not.type]({
                title: 'COLLATERAL RECORD',
                desc: not.text
            })
        }
    },
    watch: {
        search: debounce(function (search) {
            this.fetchData({ search })
        }, 500)
    },
    beforeRouteLeave (to, from, next) {
        this.$store.commit('creditOperation/collateralRecord/RESET_STATE')
        next()
    },
    beforeRouteEnter (to, from, next) {
        next(vm => {
            vm.fetchData()
        })
    }
}
</script>
